import { useState } from 'react';
import { MessageSquare, FileText, FileCode, File, ChevronDown, ChevronRight } from 'lucide-react';
import { useSessionsStore } from '../../stores/sessionsStore';
import { Spinner } from '../common/Spinner';
import type { SessionFile } from '../../types/docs';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

function shortenPath(path: string): string {
  return path
    .replace('/opt/union-threads/', '')
    .replace('/var/lib/docker/volumes/ut-secondbrain_workspace-data/_data/', 'SB:');
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.jsonl$/;

function formatFilenameDate(filename: string): string {
  if (UUID_RE.test(filename)) return 'Claude Code Session';

  // YYYY-MM-DD-HHMMSS.jsonl → "Mar 8, 2026 at 6:55 PM"
  const base = filename.replace('.jsonl', '');
  const date = new Date(
    `${base.substring(0, 10)}T${base.substring(11, 13)}:${base.substring(13, 15)}:${base.substring(15, 17)}`
  );
  if (isNaN(date.getTime())) return filename;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' at ' +
    date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

const VIEWABLE_EXTENSIONS = new Set(['md', 'txt', 'json', 'yaml', 'yml', 'toml', 'csv', 'log']);
const CODE_EXTENSIONS = new Set(['ts', 'tsx', 'js', 'jsx', 'py', 'sh', 'bash', 'css', 'scss', 'html', 'sql']);

function getFileIcon(path: string) {
  const ext = path.includes('.') ? path.split('.').pop()!.toLowerCase() : '';
  if (VIEWABLE_EXTENSIONS.has(ext)) return <FileText className="h-3 w-3 flex-shrink-0 text-text-muted" />;
  if (CODE_EXTENSIONS.has(ext)) return <FileCode className="h-3 w-3 flex-shrink-0 text-text-muted/50" />;
  return <File className="h-3 w-3 flex-shrink-0 text-text-muted/50" />;
}

function FilesChangedPanel({ files, loading }: { files: SessionFile[]; loading: boolean }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="border-t border-border-subtle">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-4 py-2 text-xs font-medium text-text-muted hover:text-text-primary transition-colors"
      >
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        Files Changed ({loading ? '...' : files.length})
      </button>
      {expanded && (
        <div className="px-4 pb-3 max-h-48 overflow-y-auto">
          {loading ? (
            <div className="flex items-center gap-2 py-2">
              <Spinner size="sm" />
              <span className="text-[10px] text-text-muted">Scanning files...</span>
            </div>
          ) : files.length === 0 ? (
            <div className="text-[10px] text-text-muted py-1">No file changes detected during this session.</div>
          ) : (
            <div className="space-y-0.5">
              {files.map(file => (
                <div
                  key={file.path}
                  className="flex items-center gap-2 py-0.5 px-1"
                >
                  {getFileIcon(file.path)}
                  <span className="text-[10px] text-text-secondary truncate flex-1">{shortenPath(file.path)}</span>
                  <span className="text-[10px] text-text-muted flex-shrink-0">{formatSize(file.size)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function SessionViewer() {
  const { selectedTranscript, transcriptLoading, sessionFiles, sessionFilesLoading } = useSessionsStore();

  if (transcriptLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="sm" />
      </div>
    );
  }

  if (!selectedTranscript) {
    return (
      <div className="flex items-center justify-center h-full text-text-muted">
        <div className="text-center">
          <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-30" />
          <div className="text-sm">Select a session to view</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-2 border-b border-border-subtle bg-bg-secondary">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-text-primary">{formatFilenameDate(selectedTranscript.filename)}</span>
          {UUID_RE.test(selectedTranscript.filename) && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-400/20 text-emerald-400 font-medium">CC</span>
          )}
        </div>
        <div className="text-[10px] text-text-muted">
          {selectedTranscript.messages.length} message{selectedTranscript.messages.length !== 1 ? 's' : ''} — {formatSize(selectedTranscript.size)}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {selectedTranscript.messages.map((msg, i) => (
          <div key={i} className="group">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-medium text-accent-purple-light capitalize">{msg.role}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-bg-tertiary text-text-muted">{msg.session_type}</span>
            </div>
            <div className="text-xs text-text-secondary whitespace-pre-wrap leading-relaxed pl-0.5">
              {msg.text}
            </div>
          </div>
        ))}
      </div>

      {/* Files Changed */}
      <FilesChangedPanel files={sessionFiles} loading={sessionFilesLoading} />
    </div>
  );
}
